import type { BoardPlacement } from "./Board.types";
import {
    getRenderedCells,
    type BoardDraggingState,
    type BoardDragModel,
} from "./boardDragState";
import { getCardDropTarget, getCellDropTarget } from "./boardDropTargets";
import { locateBoardEntry } from "./boardMove";

interface HoverDroppables {
    cellPlacements: ReadonlyMap<string, BoardPlacement>;
    stripColumnIds: ReadonlyMap<string, string>;
}

/**
 * The cell under a hovered cell droppable, or undefined over a card. A
 * collapsed column strip stands for its cell in the lane the card was picked
 * up in, wherever the pointer crossed on the way.
 */
export const getHoverCellPlacement = (
    { cellPlacements, stripColumnIds }: HoverDroppables,
    { current, overId }: { current: BoardDraggingState; overId: string },
): BoardPlacement | undefined => {
    const stripColumnId = stripColumnIds.get(overId);
    return stripColumnId === undefined
        ? cellPlacements.get(overId)
        : { columnId: stripColumnId, laneId: current.originLaneId };
};

interface HoverArgs<TItem> {
    model: BoardDragModel<TItem>;
    current: BoardDraggingState;
    overId: string;
    /** Set when the hovered droppable is a whole cell rather than a card. */
    cellPlacement: BoardPlacement | undefined;
    isAfter: boolean;
}

export const getHoverTarget = <TItem>({
    model,
    current,
    overId,
    cellPlacement,
    isAfter,
}: HoverArgs<TItem>) => {
    const previewCells = getRenderedCells(model, current);
    // Over its own copy, which a sorted board places by the order.
    const placement =
        overId === current.activeKey
            ? locateBoardEntry(previewCells, overId)?.entry.placement
            : cellPlacement;
    return placement === undefined
        ? getCardDropTarget({
              cells: previewCells,
              activePlacementKey: current.activeKey,
              overPlacementKey: overId,
              isAfter,
          })
        : getCellDropTarget({
              cells: previewCells,
              activePlacementKey: current.activeKey,
              placement,
          });
};
